import { useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Bell, MessageSquare, Search } from "lucide-react";
import type { MobileUiInboundAction } from "../../../../../packages/shared-types/src/index.js";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  getGetApiAutomationInboxNotificationIdQueryKey,
  getGetApiAutomationInboxQueryKey,
  postApiAutomationInboxNotificationIdFollowUpDraft,
  useGetApiAutomationInbox,
  useGetApiAutomationInboxNotificationId,
  usePostApiAutomationInboxNotificationIdRead,
} from "@/lib/api/generated/automations/automations";
import { postApiSessionsBootstrap } from "@/lib/api/generated/sessions/sessions";
import { ROUTES } from "@/app/routes";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useConversationsUiStore } from "@/stores/conversations-ui-store";
import { useAutomationInboxUiStore } from "@/stores/automation-inbox-ui-store";
import { AutomationResultContent } from "./automation-result-content";

function formatTimestamp(value: string) {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(timestamp);
}

function buildAutomationResultFollowUpAction(params: {
  notificationId: string;
  result: NonNullable<
    ReturnType<typeof useGetApiAutomationInboxNotificationId>["data"]
  >["result"];
}): MobileUiInboundAction {
  const { notificationId, result } = params;
  return {
    kind: "mobile.action",
    actionId: `automation-result-follow-up:${notificationId}`,
    action: "custom",
    target: {
      kind: "automation_result",
      id: result.resultId,
      label: result.automationName,
      metadata: {
        automationId: result.automationId,
        automationName: result.automationName,
        runId: result.runId,
        generatedAt: result.generatedAt,
        moduleKey: result.moduleKey,
        ownerMode: result.ownerMode,
      },
    },
    payload: {
      source: "automation_inbox",
      notificationId,
      title: result.title ?? null,
      content: result.content ?? null,
      summary: result.summary,
    },
  };
}

export function AutomationInboxPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  const { notificationId } = useParams<{ notificationId: string }>();
  const search = useAutomationInboxUiStore((state) => state.search);
  const unreadOnly = useAutomationInboxUiStore((state) => state.unreadOnly);
  const setSearch = useAutomationInboxUiStore((state) => state.setSearch);
  const setUnreadOnly = useAutomationInboxUiStore(
    (state) => state.setUnreadOnly,
  );
  const setComposerText = useConversationsUiStore(
    (state) => state.setComposerText,
  );
  const setPendingAction = useConversationsUiStore(
    (state) => state.setPendingAction,
  );

  useDocumentTitle("Automation Inbox");

  const inboxQuery = useGetApiAutomationInbox(
    { limit: 50 },
    { query: { enabled: Boolean(tenantId), refetchInterval: 15_000 } },
  );

  const notifications = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (inboxQuery.data?.notifications ?? []).filter((notification) => {
      if (unreadOnly && notification.status !== "unread") {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [notification.automationName, notification.summary]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [inboxQuery.data?.notifications, search, unreadOnly]);

  const selectedNotificationId =
    notificationId ?? notifications[0]?.notificationId;
  const detailQuery = useGetApiAutomationInboxNotificationId(
    selectedNotificationId ?? "",
    {
      query: { enabled: Boolean(tenantId && selectedNotificationId) },
    },
  );

  const markReadMutation = usePostApiAutomationInboxNotificationIdRead({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getGetApiAutomationInboxQueryKey(),
        });
        if (selectedNotificationId) {
          await queryClient.invalidateQueries({
            queryKey: getGetApiAutomationInboxNotificationIdQueryKey(
              selectedNotificationId,
            ),
          });
        }
      },
    },
  });

  const followUpMutation = useMutation({
    mutationFn: async (nextNotificationId: string) => {
      const detail = detailQuery.data;
      if (
        !detail ||
        detail.notification.notificationId !== nextNotificationId
      ) {
        throw new Error("The selected automation result is not loaded yet.");
      }
      const draft =
        await postApiAutomationInboxNotificationIdFollowUpDraft(
          nextNotificationId,
        );
      const bootstrap = await postApiSessionsBootstrap({});
      const targetSession = bootstrap.visibleAgents.find(
        (entry) => entry.agentId === draft.agentId,
      );
      if (!targetSession) {
        throw new Error(
          "No visible conversation session exists for this automation agent.",
        );
      }

      return {
        draft,
        action: buildAutomationResultFollowUpAction({
          notificationId: nextNotificationId,
          result: detail.result,
        }),
        sessionId: targetSession.sessionId,
      };
    },
    onSuccess: ({ draft, action, sessionId }) => {
      setComposerText(sessionId, draft.promptText);
      setPendingAction(sessionId, action);
      navigate(
        `${ROUTES.tenantConversations(tenantId)}?session=${encodeURIComponent(sessionId)}`,
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to prepare follow-up draft.",
      );
    },
  });

  useEffect(() => {
    if (!tenantId || !notifications.length || notificationId) {
      return;
    }

    navigate(
      ROUTES.tenantAutomationInboxDetail(
        tenantId,
        notifications[0].notificationId,
      ),
      { replace: true },
    );
  }, [navigate, notificationId, notifications, tenantId]);

  useEffect(() => {
    if (
      !selectedNotificationId ||
      !detailQuery.data ||
      detailQuery.data.notification.status !== "unread" ||
      markReadMutation.isPending
    ) {
      return;
    }

    void markReadMutation.mutateAsync({
      notificationId: selectedNotificationId,
    });
  }, [detailQuery.data, markReadMutation, selectedNotificationId]);

  if (!tenantId) {
    return null;
  }

  if (inboxQuery.isLoading) {
    return (
      <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div
          className="h-[calc(100vh-11rem)] rounded-md border"
          style={{ borderColor: "var(--border)" }}
        />
        <div
          className="h-[calc(100vh-11rem)] rounded-md border"
          style={{ borderColor: "var(--border)" }}
        />
      </div>
    );
  }

  if (
    notifications.length === 0 &&
    (inboxQuery.data?.notifications?.length ?? 0) === 0
  ) {
    return (
      <EmptyState
        icon={<Bell size={40} strokeWidth={1.2} />}
        title="No automation inbox items"
        description="Inbox deliveries will appear here when an automation publishes a result to the current user."
        action={{
          label: "Open automations",
          onClick: () => navigate(ROUTES.tenantAutomations(tenantId)),
        }}
      />
    );
  }

  const detail = detailQuery.data;

  return (
    <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
      <Card className="h-[calc(100vh-11rem)] overflow-hidden">
        <CardHeader className="border-b border-border gap-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Automation Inbox</CardTitle>
            <Badge variant="outline">
              {inboxQuery.data?.unreadCount ?? 0} unread
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2"
                style={{ color: "var(--muted-foreground)" }}
              />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search inbox"
                className="pl-9"
              />
            </div>
            <Button
              variant={unreadOnly ? "default" : "outline"}
              onClick={() => setUnreadOnly(!unreadOnly)}
            >
              Unread
            </Button>
          </div>
        </CardHeader>
        <CardContent className="h-[calc(100%-6.5rem)] overflow-y-auto p-3">
          <div className="grid gap-2">
            {notifications.length === 0 ? (
              <EmptyState
                title="No matching inbox items"
                description="Try clearing the current search or unread filter."
              />
            ) : (
              notifications.map((notification) => {
                const isSelected =
                  notification.notificationId === selectedNotificationId;
                return (
                  <button
                    key={notification.notificationId}
                    type="button"
                    onClick={() =>
                      navigate(
                        ROUTES.tenantAutomationInboxDetail(
                          tenantId,
                          notification.notificationId,
                        ),
                      )
                    }
                    className="rounded-md border px-3 py-3 text-left transition-colors"
                    style={{
                      borderColor: isSelected ? "var(--ring)" : "var(--border)",
                      background: isSelected
                        ? "var(--bg-elevated)"
                        : "transparent",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="truncate text-sm font-medium"
                            style={{ color: "var(--text-strong)" }}
                          >
                            {notification.automationName}
                          </span>
                          {notification.status === "unread" ? (
                            <Badge variant="active">new</Badge>
                          ) : null}
                        </div>
                        <p
                          className="mt-1 line-clamp-2 text-xs"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          {notification.summary}
                        </p>
                      </div>
                      <span
                        className="shrink-0 text-[11px]"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        {formatTimestamp(notification.createdAt)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="h-[calc(100vh-11rem)] overflow-hidden">
        {!detail ? (
          <CardContent className="flex h-full items-center justify-center p-6">
            <EmptyState
              title="Select an inbox item"
              description="Choose a delivery on the left to inspect the rich automation result and continue the conversation."
            />
          </CardContent>
        ) : (
          <>
            <CardHeader className="border-b border-border gap-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <CardTitle className="text-base">
                    {detail.notification.automationName}
                  </CardTitle>
                  <p
                    className="mt-1 text-sm"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    {detail.notification.summary}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      detail.notification.status === "unread"
                        ? "active"
                        : "outline"
                    }
                  >
                    {detail.notification.status}
                  </Badge>
                  <Badge variant="outline">{detail.result.ownerMode}</Badge>
                </div>
              </div>
              <div
                className="flex flex-wrap items-center gap-2 text-xs"
                style={{ color: "var(--muted-foreground)" }}
              >
                <span>
                  Generated {formatTimestamp(detail.result.generatedAt)}
                </span>
                <span>Run {detail.result.runId}</span>
                <span>Agent {detail.result.agentId}</span>
              </div>
            </CardHeader>
            <CardContent className="grid h-[calc(100%-6.5rem)] gap-4 overflow-y-auto p-4">
              <AutomationResultContent result={detail.result} />

              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-3"
                style={{
                  borderColor: "var(--border)",
                  background: "var(--bg)",
                }}
              >
                <div>
                  <div
                    className="text-sm font-medium"
                    style={{ color: "var(--text-strong)" }}
                  >
                    Continue with the agent
                  </div>
                  <div
                    className="mt-0.5 text-xs"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Opens the source conversation with this result attached to
                    the next message.
                  </div>
                </div>
                <Button
                  onClick={() =>
                    void followUpMutation.mutateAsync(
                      detail.notification.notificationId,
                    )
                  }
                  disabled={followUpMutation.isPending}
                >
                  <MessageSquare className="size-4" />
                  Open In Conversation
                </Button>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
